// src/pages/Inventory/StockInModal.js
import React, {Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { inventoryService } from '../../services/api';
import { toast } from 'react-toastify';
import SearchableSelect from "../../components/ui/SearchableSelect";

const StockInModal = ({ isOpen, onClose, onSuccess, partTypes, products }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [selectedPartType, setSelectedPartType] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        watch,
        setValue,
        getValues,
    } = useForm();

    const watchPartTypeId = watch('part_type_id');

    // Reset form when modal is opened or closed
    useEffect(() => {
        if (isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    // Watch for part type changes to determine if it's SKU-specific
    useEffect(() => {
        if (watchPartTypeId) {
            const partType = partTypes.find(pt => pt.id === parseInt(watchPartTypeId));
            setSelectedPartType(partType);

            // If switching from SKU-specific to non-SKU-specific, clear product_id
            if (partType && !partType.is_sku_specific) {
                setValue('product_id', '');
            }
        } else {
            setSelectedPartType(null);
        }
    }, [watchPartTypeId, partTypes, setValue]);

    // Handler for part type selection
    const handlePartTypeChange = (partTypeId) => {
        setValue('part_type_id', partTypeId, { shouldValidate: true });
    };

    // Handler for product selection
    const handleProductChange = (productId) => {
        setValue('product_id', productId, { shouldValidate: true });
    };

    const onSubmit = async (data) => {
        setIsLoading(true);

        try {
            // Convert string IDs to numbers
            data.part_type_id = parseInt(data.part_type_id);
            data.quantity = parseInt(data.quantity);

            if (data.product_id) {
                data.product_id = parseInt(data.product_id);
            }

            await inventoryService.stockIn(data);
            toast.success('Stock added successfully');
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Error adding stock:', error);
            toast.error(error.response?.data?.message || 'Failed to add stock');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <div>
                                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                        Stock In
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Add inventory to your stock. Select a part type and enter the quantity to add.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit(onSubmit)} className="mt-5 space-y-4">
                                <div>
                                    <label htmlFor="part_type_id" className="block text-sm font-medium text-gray-700 mb-1">
                                        Part Type *
                                    </label>

                                    <input
                                        type="hidden"
                                        {...register('part_type_id', { required: 'Part type is required' })}
                                    />

                                    <SearchableSelect
                                        id="part_type_id"
                                        options={partTypes.map(partType => ({
                                            id: partType.id,
                                            label: `${partType.name} ${partType.is_sku_specific ? '(SKU-specific)' : ''}`
                                        }))}
                                        value={getValues('part_type_id')}
                                        onChange={handlePartTypeChange}
                                        placeholder="Select a part type"
                                    />

                                    {errors.part_type_id && (
                                        <p className="mt-1 text-sm text-red-600">{errors.part_type_id.message}</p>
                                    )}
                                </div>

                                {selectedPartType && selectedPartType.is_sku_specific && (
                                    <div>
                                        <label htmlFor="product_id" className="block text-sm font-medium text-gray-700 mb-1">
                                            Product SKU *
                                        </label>

                                        <input
                                            type="hidden"
                                            {...register('product_id', {
                                                required: selectedPartType?.is_sku_specific ? 'Product SKU is required for SKU-specific parts' : false
                                            })}
                                        />

                                        <SearchableSelect
                                            id="product_id"
                                            options={products.map(product => ({
                                                id: product.id,
                                                label: `${product.sku} ${product.description ? '- ' + product.description : ''}`
                                            }))}
                                            value={getValues('product_id')}
                                            onChange={handleProductChange}
                                            placeholder="Select a product SKU"
                                        />

                                        {errors.product_id && (
                                            <p className="mt-1 text-sm text-red-600">{errors.product_id.message}</p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700">
                                        Quantity *
                                    </label>
                                    <input
                                        type="number"
                                        id="quantity"
                                        {...register('quantity', {
                                            required: 'Quantity is required',
                                            min: { value: 1, message: 'Quantity must be at least 1' }
                                        })}
                                        className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md ${
                                            errors.quantity ? 'border-red-300' : ''
                                        }`}
                                        placeholder="Enter quantity"
                                    />
                                    {errors.quantity && (
                                        <p className="mt-1 text-sm text-red-600">{errors.quantity.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="reference_number" className="block text-sm font-medium text-gray-700">
                                        Reference Number
                                    </label>
                                    <input
                                        type="text"
                                        id="reference_number"
                                        {...register('reference_number')}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                        placeholder="PO number, shipment number, etc."
                                    />
                                </div>

                                <div>
                                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                                        Notes
                                    </label>
                                    <textarea
                                        id="notes"
                                        rows={3}
                                        {...register('notes')}
                                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                                        placeholder="Add any notes about this stock in transaction"
                                    />
                                </div>

                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                                    >
                                        {isLoading ? 'Processing...' : 'Add Stock'}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                        onClick={onClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default StockInModal;
